/**
 * How long an invitation has left.
 *
 * `Date.now()` is not callable during render — the clock moves, so a component
 * that reads it is not idempotent and React's lint rules say so. It is captured
 * once per mount instead, which is not a workaround but the right answer: an
 * invitation has fourteen days on it, nobody is watching the countdown tick, and
 * a value that changed under a re-render would be worse than one that does not.
 */
import { useState } from 'react';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The wall clock, sampled when the component mounted. */
export const useNow = (): number => {
  const [now] = useState(() => Date.now());
  return now;
};

export type Expiry = { expired: boolean; daysLeft: number };

/**
 * Rounded up and floored at one: "expires in 0 days" is a thing no interface
 * should say, and the last day of an invitation is still a day it works.
 */
export const expiryOf = (expiresAt: number, now: number): Expiry => ({
  expired: expiresAt < now,
  daysLeft: Math.max(1, Math.ceil((expiresAt - now) / DAY_MS)),
});
